import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';

// Overdracht for a receiver: what they took over (as a checklist), the notes per function
// and a button to confirm they have gone through it. Admins see who has confirmed.

const fmtDate = v => (v ? new Date(`${v.replace(' ', 'T')}${v.includes('T') ? '' : 'Z'}`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function HandoverView({ id }) {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const load = () => api(`/api/handovers/${id}`).then(setData)
    .catch(err => setError(err.code === 'not_found' ? 'Deze overdracht bestaat niet of is niet voor jou.' : 'De overdracht kon niet worden geladen. Probeer het later opnieuw.'));
  useEffect(() => { setData(null); setChecked({}); load(); }, [id]);

  async function confirm() {
    setBusy(true); setConfirmError(null);
    try { await api(`/api/handovers/${id}/acknowledge`, { method: 'POST' }); await load(); } catch { setConfirmError('Bevestigen lukte niet. Probeer het opnieuw.'); }
    setBusy(false);
  }

  if (error) return <div class="alert alert-error">{error}</div>;
  if (!data) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  const mine = data.items.filter(i => i.to_user_id === auth.userId);
  const others = data.items.filter(i => i.to_user_id !== auth.userId);
  const groups = list => list.reduce((acc, i) => {
    const g = acc.find(x => x.type === i.type) || (acc.push({ type: i.type, label: i.type_label, items: [] }), acc[acc.length - 1]);
    g.items.push(i);
    return acc;
  }, []);
  const allChecked = mine.every(i => checked[i.id]);

  return (
    <div style="max-width:820px">
      <div style="margin-bottom:16px">
        <h1 class="page-title">🤝 Overdracht van {data.from_name || 'oud-lid'}</h1>
        <div class="text-sm text-muted">Op {fmtDate(data.created_at)} door {data.created_by_name || 'een beheerder'} · opvolger {data.to_name || 'oud-lid'}</div>
      </div>
      <div style="display:flex;flex-direction:column;gap:16px">
        {data.note && (
          <Card title="Notitie">
            <div style="white-space:pre-line;font-size:14px;overflow-wrap:anywhere">{data.note}</div>
          </Card>
        )}

        {data.notes.length > 0 && (
          <Card title="📝 Dit moet je weten">
            {data.notes.map(n => (
              <div key={n.category_id} style="margin-bottom:12px">
                <div style="font-weight:600;font-size:14px">{n.category}</div>
                {n.history.length === 0 && <div class="text-sm text-muted">Nog geen notitie bij deze functie.</div>}
                {n.history.map((x, idx) => (
                  <div key={x.id} style={`font-size:14px;margin-top:4px;${idx > 0 ? 'opacity:.7' : ''}`}>
                    <div style="white-space:pre-line;overflow-wrap:anywhere">{x.note}</div>
                    <div class="text-sm text-muted">{x.written_by_name || 'Onbekend'} · {fmtDate(x.created_at)}{idx > 0 ? ' · eerdere notitie' : ''}</div>
                  </div>
                ))}
              </div>
            ))}
          </Card>
        )}

        {data.is_receiver && (
          <Card title={`✅ Wat jij overneemt (${mine.length})`}>
            {mine.length === 0 && <div class="text-sm text-muted">Je hebt geen onderdelen overgenomen; lees de notities hierboven door.</div>}
            {groups(mine).map(g => (
              <div key={g.type} style="margin-bottom:10px">
                <div style="font-size:13px;font-weight:600;margin-bottom:4px">{g.label}</div>
                {g.items.map(i => (
                  <label key={i.id} style="display:flex;gap:8px;align-items:flex-start;font-size:14px;padding:3px 0;cursor:pointer">
                    <input type="checkbox" checked={!!checked[i.id] || !!data.acknowledged} disabled={!!data.acknowledged}
                      onChange={e => setChecked(c => ({ ...c, [i.id]: e.target.checked }))} style="margin-top:3px" />
                    <span style="overflow-wrap:anywhere">
                      {i.label}{i.link && <> · <a href={i.link}>bekijken</a></>}
                      {i.remark && <div class="text-sm text-muted" style="white-space:pre-line">💬 {i.remark}</div>}
                    </span>
                  </label>
                ))}
              </div>
            ))}
            <div style="margin-top:8px;display:flex;gap:10px;align-items:center;flex-wrap:wrap">
              {data.acknowledged
                ? <span class="text-sm" style="color:var(--color-success,#16a34a)">✓ Bevestigd op {fmtDate(data.acknowledged)}</span>
                : <>
                    <Button onClick={confirm} disabled={busy || !allChecked}>Ik heb alles doorgenomen</Button>
                    {!allChecked && <span class="text-sm text-muted">Vink eerst alles af.</span>}
                    {confirmError && <span class="text-sm" style="color:var(--color-danger)">{confirmError}</span>}
                  </>}
            </div>
          </Card>
        )}

        {others.length > 0 && (
          <Card title={data.is_receiver ? `Naar anderen (${others.length})` : `Overgedragen (${others.length})`}>
            {groups(others).map(g => (
              <div key={g.type} style="margin-bottom:10px">
                <div style="font-size:13px;font-weight:600;margin-bottom:4px">{g.label}</div>
                {g.items.map(i => (
                  <div key={i.id} style="font-size:14px;padding:2px 0;overflow-wrap:anywhere">
                    {i.label} → {i.to_name || 'oud-lid'}
                    {i.remark && <div class="text-sm text-muted" style="white-space:pre-line">💬 {i.remark}</div>}
                  </div>
                ))}
              </div>
            ))}
          </Card>
        )}

        <Card title="Bevestigd door">
          {data.receipts.map(r => (
            <div key={r.user_id} style="font-size:14px;padding:2px 0">
              {r.acknowledged ? '✓' : '…'} {r.display_name || 'Oud-lid'} <span class="text-sm text-muted">{r.acknowledged ? fmtDate(r.acknowledged_at) : 'nog niet bevestigd'}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
